
import { useState, useEffect, useCallback } from 'react';


const isElectron = () => {
    return window.electronAPI !== undefined;
};


export const useLoans = (filters = {}, userContext = null) => {
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchLoans = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {

            const filtersWithContext = {
                ...filters,
                ...(userContext && {
                    role: userContext.role,
                    userId: userContext.userId
                })
            };
            const result = await window.electronAPI.loan.getAll(filtersWithContext);
            if (result.success) {
                setLoans(result.data || []);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [JSON.stringify(filters), userContext?.role, userContext?.userId]);

    useEffect(() => {
        fetchLoans();
    }, [fetchLoans]);

    return { loans, loading, error, refetch: fetchLoans };
};


export const useWaivers = (filters = {}, userContext = null) => {
    const [waivers, setWaivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchWaivers = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const filtersWithContext = {
                ...filters,
                ...(userContext && {
                    role: userContext.role,
                    userId: userContext.userId
                })
            };
            const result = await window.electronAPI.waiver.getAll(filtersWithContext);
            if (result.success) {
                setWaivers(result.data || []);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [JSON.stringify(filters), userContext?.role, userContext?.userId]);

    useEffect(() => {
        fetchWaivers();
    }, [fetchWaivers]);

    return { waivers, loading, error, refetch: fetchWaivers };
};


export const useWaiver = (id) => {
    const [waiver, setWaiver] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchWaiver = useCallback(async () => {
        if (!isElectron() || !id) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.waiver.getById(parseInt(id));
            if (result.success) {
                setWaiver(result.data);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchWaiver();
    }, [fetchWaiver]);

    return { waiver, loading, error, refetch: fetchWaiver };
};


export const useWaiverStats = (userContext = null) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStats = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const filters = userContext ? {
                role: userContext.role,
                userId: userContext.userId
            } : {};
            const result = await window.electronAPI.waiver.getStats(filters);
            if (result.success) {
                setStats(result.data);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [userContext?.role, userContext?.userId]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    return { stats, loading, error, refetch: fetchStats };
};


export const useWaiverMutations = () => {
    const createWaiver = async (waiverData, userId, userName) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.waiver.create(waiverData, userId, userName);
    };

    const updateWaiver = async (id, waiverData) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.waiver.update(id, waiverData);
    };

    const deleteWaiver = async (id) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.waiver.delete(id);
    };

    const submitForReview = async (id, userId, userName) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.waiver.submitForReview(id, userId, userName);
    };

    const openForVoting = async (id, userId, userName) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.waiver.openForVoting(id, userId, userName);
    };

    const startNegotiation = async (id, userId, userName) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.waiver.startNegotiation(id, userId, userName);
    };

    const castVote = async (id, vote, userId, userName, participationPercentage, comment) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.waiver.castVote(id, vote, userId, userName, participationPercentage, comment);
    };

    const addComment = async (id, text, userId, userName, userRole) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.waiver.addComment(id, text, userId, userName, userRole);
    };

    const addDocument = async (id, documentData, userId) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.waiver.addDocument(id, documentData, userId);
    };

    const addNegotiationProposal = async (id, proposalData, userId, userName, userRole) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.waiver.addNegotiationProposal(id, proposalData, userId, userName, userRole);
    };

    const acceptNegotiationProposal = async (waiverId, proposalId, userId, userName) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.waiver.acceptNegotiationProposal(waiverId, proposalId, userId, userName);
    };

    return {
        createWaiver,
        updateWaiver,
        deleteWaiver,
        submitForReview,
        openForVoting,
        startNegotiation,
        castVote,
        addComment,
        addDocument,
        addNegotiationProposal,
        acceptNegotiationProposal
    };
};


export const useLoan = (id) => {
    const [loan, setLoan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchLoan = useCallback(async () => {
        if (!isElectron() || !id) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.loan.getById(parseInt(id));
            if (result.success) {
                setLoan(result.data);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchLoan();
    }, [fetchLoan]);

    return { loan, loading, error, refetch: fetchLoan };
};


export const useLoanStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStats = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.loan.getStats();
            if (result.success) {
                setStats(result.data);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    return { stats, loading, error, refetch: fetchStats };
};


export const useAlerts = (filters = {}) => {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAlerts = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.alert.getAll(filters);
            if (result.success) {
                setAlerts(result.data || []);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [JSON.stringify(filters)]);

    useEffect(() => {
        fetchAlerts();
    }, [fetchAlerts]);

    const markAsRead = async (id) => {
        if (!isElectron()) return;
        const result = await window.electronAPI.alert.markAsRead(id);
        if (result.success) {
            fetchAlerts();
        }
        return result;
    };

    const markAllAsRead = async () => {
        if (!isElectron()) return;
        const result = await window.electronAPI.alert.markAllAsRead();
        if (result.success) {
            fetchAlerts();
        }
        return result;
    };

    const archiveAlert = async (id) => {
        if (!isElectron()) return;
        const result = await window.electronAPI.alert.archive(id);
        if (result.success) {
            fetchAlerts();
        }
        return result;
    };

    return {
        alerts,
        loading,
        error,
        refetch: fetchAlerts,
        markAsRead,
        markAllAsRead,
        archiveAlert
    };
};


export const useAlertStats = () => {
    const [stats, setStats] = useState({ unread: 0, critical: 0, warning: 0, info: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStats = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.alert.getStats();
            if (result.success) {
                setStats(result.data);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    return { stats, loading, error, refetch: fetchStats };
};


export const useCovenants = (loanId) => {
    const [covenants, setCovenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchCovenants = useCallback(async () => {
        if (!isElectron() || !loanId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.covenant.getByLoanId(parseInt(loanId));
            if (result.success) {
                setCovenants(result.data || []);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [loanId]);

    useEffect(() => {
        fetchCovenants();
    }, [fetchCovenants]);

    return { covenants, loading, error, refetch: fetchCovenants };
};


export const useLoanMutations = () => {
    const createLoan = async (loanData, userId) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.loan.create(loanData, userId);
    };

    const updateLoan = async (id, loanData) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.loan.update(id, loanData);
    };

    const deleteLoan = async (id) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.loan.delete(id);
    };

    const archiveLoan = async (id) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.loan.archive(id);
    };

    return { createLoan, updateLoan, deleteLoan, archiveLoan };
};


export const useCovenantMutations = () => {
    const createCovenant = async (covenantData, userId) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.covenant.create(covenantData, userId);
    };

    const updateCovenant = async (id, covenantData) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.covenant.update(id, covenantData);
    };

    const updateCovenantValue = async (id, value, userId, notes) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.covenant.updateValue(id, value, userId, notes);
    };

    const deleteCovenant = async (id) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.covenant.delete(id);
    };

    return { createCovenant, updateCovenant, updateCovenantValue, deleteCovenant };
};


export const useIsElectron = () => {
    return isElectron();
};


export const useDocuments = (loanId) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchDocuments = useCallback(async () => {
        if (!isElectron() || !loanId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.document.getByLoanId(parseInt(loanId));
            if (result.success) {
                setDocuments(result.data || []);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [loanId]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    return { documents, loading, error, refetch: fetchDocuments };
};


export const useCovenantHistory = (loanId) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!isElectron() || !loanId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.covenant.getHistoryByLoan(parseInt(loanId));
            if (result.success) {
                setHistory(result.data || []);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [loanId]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    return { history, loading, error, refetch: fetchHistory };
};


export const useActivityLogs = (loanId) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchLogs = useCallback(async () => {
        if (!isElectron() || !loanId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.activity.getByLoan(parseInt(loanId));
            if (result.success) {
                setLogs(result.data || []);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [loanId]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    return { logs, loading, error, refetch: fetchLogs };
};


export const useDocumentMutations = () => {
    const uploadDocument = async (documentData, userId) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.document.create(documentData, userId);
    };

    const deleteDocument = async (id) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.document.delete(id);
    };

    return { uploadDocument, deleteDocument };
};


export const useImportHistory = (limit = 50) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.importHistory.getAll(limit);
            if (result.success) {
                setHistory(result.data || []);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [limit]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    return { history, loading, error, refetch: fetchHistory };
};


export const useImportHistoryMutations = () => {
    const createImportRecord = async (importData, userId) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.importHistory.create(importData, userId);
    };

    const updateImportStatus = async (id, status, errorMessage) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.importHistory.updateStatus(id, status, errorMessage);
    };

    const updateImportCounts = async (id, recordsImported, recordsSkipped) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.importHistory.updateCounts(id, recordsImported, recordsSkipped);
    };

    const deleteImportRecord = async (id) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.importHistory.delete(id);
    };

    return { createImportRecord, updateImportStatus, updateImportCounts, deleteImportRecord };
};


export const useAnalytics = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAnalytics = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.analytics.getDashboard();
            if (result.success) {
                setData(result.data);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    return { data, loading, error, refetch: fetchAnalytics };
};


export const useComplianceHistory = (months = 6) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.analytics.getComplianceHistory(months);
            if (result.success) {
                setHistory(result.data || []);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [months]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    return { history, loading, error, refetch: fetchHistory };
};


export const useAlertsHistory = (months = 6) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.analytics.getAlertsHistory(months);
            if (result.success) {
                setHistory(result.data || []);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [months]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    return { history, loading, error, refetch: fetchHistory };
};


export const useAvgMaturity = () => {
    const [maturity, setMaturity] = useState({ display: 'N/A' });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchMaturity = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.analytics.getAvgMaturity();
            if (result.success) {
                setMaturity(result.data || { display: 'N/A' });
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMaturity();
    }, [fetchMaturity]);

    return { maturity, loading, error, refetch: fetchMaturity };
};


export const useAtRiskLoans = (limit = 5) => {
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchLoans = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.analytics.getAtRiskLoans(limit);
            if (result.success) {
                setLoans(result.data || []);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [limit]);

    useEffect(() => {
        fetchLoans();
    }, [fetchLoans]);

    return { loans, loading, error, refetch: fetchLoans };
};


export const useAlertMutations = () => {
    const markAsRead = async (id) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.alert.markAsRead(id);
    };

    const markAllAsRead = async () => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.alert.markAllAsRead();
    };

    const archiveAlert = async (id) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        return await window.electronAPI.alert.archive(id);
    };

    return { markAsRead, markAllAsRead, archiveAlert };
};


export const useSectors = () => {
    const [sectors, setSectors] = useState([]);

    useEffect(() => {
        const fetchSectors = async () => {
            if (!isElectron()) return;
            try {
                const result = await window.electronAPI.loan.getSectors();
                if (result.success) {
                    setSectors(result.data || []);
                }
            } catch (err) {
                console.error('Error fetching sectors:', err);
            }
        };
        fetchSectors();
    }, []);

    return sectors;
};


export const useCountries = () => {
    const [countries, setCountries] = useState([]);

    useEffect(() => {
        const fetchCountries = async () => {
            if (!isElectron()) return;
            try {
                const result = await window.electronAPI.loan.getCountries();
                if (result.success) {
                    setCountries(result.data || []);
                }
            } catch (err) {
                console.error('Error fetching countries:', err);
            }
        };
        fetchCountries();
    }, []);

    return countries;
};


export const useProfiles = () => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchProfiles = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.profile?.getAll?.();
            if (result?.success) {
                setProfiles(result.data || []);
            } else {
                setProfiles([]);
            }
        } catch (err) {
            console.error('Error fetching profiles:', err);
            setError(err.message);
            setProfiles([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProfiles();
    }, [fetchProfiles]);

    return { profiles, loading, error, refetch: fetchProfiles };
};


export const useProfileMutations = () => {
    const updateProfile = async (id, updates) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        try {
            const result = await window.electronAPI.profile?.update?.(id, updates);
            return result || { success: false, error: 'Profile API not available' };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    const deleteProfile = async (id) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        try {
            const result = await window.electronAPI.profile?.delete?.(id);
            return result || { success: false, error: 'Profile API not available' };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    return { updateProfile, deleteProfile };
};


export const useInvitations = () => {
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchInvitations = useCallback(async () => {
        if (!isElectron()) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await window.electronAPI.invitation?.getAll?.();
            if (result?.success) {
                setInvitations(result.data || []);
            } else {
                setInvitations([]);
            }
        } catch (err) {
            console.error('Error fetching invitations:', err);
            setError(err.message);
            setInvitations([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInvitations();
    }, [fetchInvitations]);

    return { invitations, loading, error, refetch: fetchInvitations };
};


export const useInvitationMutations = () => {
    const createInvitation = async (email, role, invitedBy, options = {}) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        try {
            const result = await window.electronAPI.invitation?.create?.(email, role, invitedBy, options);
            return result || { success: false, error: 'Invitation API not available' };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    const cancelInvitation = async (id) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        try {
            const result = await window.electronAPI.invitation?.cancel?.(id);
            return result || { success: false, error: 'Invitation API not available' };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    const resendInvitation = async (id, options = {}) => {
        if (!isElectron()) return { success: false, error: 'Not in Electron' };
        try {
            const result = await window.electronAPI.invitation?.resend?.(id, options);
            return result || { success: false, error: 'Invitation API not available' };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    return { createInvitation, cancelInvitation, resendInvitation };
};